<script setup>
import { ref, computed } from 'vue'
import { useStore } from 'vuex'
import { API, HOST } from '@/utils/http'
import { useRoute, useRouter } from 'vue-router'
import { toast } from '@/components/ui/toast'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Brain, Settings, RotateCcw, ChevronDown, ChevronUp, Plus } from 'lucide-vue-next'

const props = defineProps({
  layer: {
    type: Object,
    required: true
  }
})

const route = useRoute()
const router = useRouter()
const store = useStore()
const orgId = computed(() => route.params.org_id)
const layerId = computed(() => route.params.layer_id)
const isGeneratingReport = ref(false)
const systemPrompt = ref('')
const userPrompt = ref('')
const showDebugSettings = ref(false)
const aiConfig = ref({})
const isLoadingConfig = ref(false)

const layerCode = computed(() => {
  return props.layer?.uuid
})

const loadAIConfig = async () => {
  isLoadingConfig.value = true
  try {
    const response = await API({
      method: 'GET',
      url: `${HOST}/org/${orgId.value}/reports/ai-config`
    })

    aiConfig.value = response.data

    if (!systemPrompt.value) {
      systemPrompt.value = aiConfig.value.default_system_prompt || defaultSystemPrompt
    }
    if (!userPrompt.value) {
      userPrompt.value = aiConfig.value.default_user_prompt || defaultUserPrompt
    }
  } catch (error) {
    console.error('Error loading AI configuration:', error)
    initializePrompts()
  } finally {
    isLoadingConfig.value = false
  }
}

const initializePrompts = () => {
  if (!systemPrompt.value) {
    systemPrompt.value = defaultSystemPrompt
  }
  if (!userPrompt.value) {
    userPrompt.value = defaultUserPrompt
  }
}

const resetPrompts = () => {
  systemPrompt.value = aiConfig.value.default_system_prompt || defaultSystemPrompt
  userPrompt.value = aiConfig.value.default_user_prompt || defaultUserPrompt
}

const toggleDebugSettings = async () => {
  showDebugSettings.value = !showDebugSettings.value
  if (showDebugSettings.value) {
    // Load AI config if not already loaded
    if (!aiConfig.value.default_system_prompt) {
      await loadAIConfig()
    } else {
      initializePrompts()
    }
  }
}

const generateAndSaveReport = async () => {
  if (!layerCode.value || !orgId.value) return

  isGeneratingReport.value = true
  try {
    const selectedRange = store.getters['layers/getSelectedRange']

    let url = `${HOST}/org/${orgId.value}/reports/layers/${layerCode.value}`
    const params = {}
    const data = {}

    if (selectedRange && Array.isArray(selectedRange) && selectedRange.length >= 2) {
      params.startDate = selectedRange[0]
      params.endDate = selectedRange[1]
    }

    if (systemPrompt.value?.trim()) {
      data.system_prompt = systemPrompt.value
    }

    if (userPrompt.value?.trim()) {
      data.user_prompt = userPrompt.value
    }

    const response = await API({
      method: 'POST',
      url: url,
      params: params,
      data: data
    })

    console.log(response)

    const newReport = response.data

    toast({
      title: 'Report Started',
      description: 'Redirecting to report page...',
      variant: 'default'
    })

    userPrompt.value = ''

    router.push(`/organizations/${orgId.value}/reports/${newReport.code}`)
  } catch (error) {
    console.error('Error starting AI report generation:', error)

    // More specific error messages
    let errorMessage = 'Failed to start AI report generation'
    if (error.response?.status === 500) {
      errorMessage = 'AI service unavailable. Please try again later.'
    } else if (error.response?.status === 404) {
      errorMessage = 'No features found for analysis'
    }

    toast({
      title: 'AI Report Failed',
      description: errorMessage,
      variant: 'destructive'
    })
  } finally {
    isGeneratingReport.value = false
  }
}

// Load AI configuration when component mounts
loadAIConfig()
</script>

<template>
  <div class="space-y-3">
    <div class="border rounded-lg p-2 mb-5">
      <div class="flex items-center justify-between cursor-pointer" @click="toggleDebugSettings">
        <div class="flex items-center gap-2">
          <Settings class="h-4 w-4" />
          <Label class="text-sm font-medium">Debug Settings</Label>
          <span v-if="isLoadingConfig" class="text-xs text-gray-500">(Loading...)</span>
        </div>
        <ChevronDown v-if="!showDebugSettings" class="h-4 w-4" />
        <ChevronUp v-else class="h-4 w-4 text-purple-600" />
      </div>

      <div v-if="showDebugSettings" class="mt-4">
        <div class="space-y-2">
          <div v-if="aiConfig.model">Model: {{ aiConfig.model }}</div>
          <div>
            <div class="flex items-center justify-between mb-2">
              <Label class="text-sm font-medium">System Prompt</Label>
              <Button
                @click="resetPrompts"
                variant="ghost"
                size="sm"
                class="text-xs"
              >
                <RotateCcw class="h-3 w-3 mr-1" />
                Reset
              </Button>
            </div>
            <Textarea
              v-model="systemPrompt"
              placeholder="Enter your system prompt..."
              class="min-h-[150px] text-sm font-mono"
              rows="8"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- User Input and Generate Report Button -->
    <div class="space-y-2">
      <div class="flex gap-2">
        <Textarea
          v-model="userPrompt"
          placeholder="What specific analysis would you like?"
          class="flex-1 min-h-[80px]"
          rows="3"
        />
      </div>
      <div class="flex justify-end">
        <Button
          @click="generateAndSaveReport"
          :disabled="isGeneratingReport"
          variant="default"
          size="sm"
          class="bg-gradient-to-r from-purple-600 to-blue-600 text-white hover:from-purple-700 hover:to-blue-700 whitespace-nowrap"
        >
          <Brain class="h-4 w-4 mr-2" />
          <span v-if="isGeneratingReport">Generating...</span>
          <span v-else>New Report</span>
        </Button>
      </div>
    </div>

    <div>
      <div class="text-center py-4 text-muted-foreground">
        <p class="text-sm">
          <router-link
            :to="`/organizations/${orgId}/reports`"
            class="text-primary hover:underline font-medium"
          >
            Reports History
          </router-link>
        </p>
      </div>
    </div>
  </div>
</template>
